import React, { useRef, useState, useCallback, ReactNode } from 'react';
import PopNotification from './components/PopNotification';
import { PopNotificationRef } from './components/PopNotification/PopNotification.component';
import PopNotificationsContext, {
  Notification,
} from './PopNotifications.context';
import uniqid from 'uniqid';
import { AxiosError } from 'axios';
import { getAxiosErrorId, getMixpanelErrorProps } from 'axiosUtils';
import { useTranslation } from 'react-i18next';
import { ErrorMessageValue, ERRORS_USER_FRIENDLY } from 'request-responses';
import GenericErrorContent from './components/GenericErrorContent';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { ErrorViewedProps } from 'models/MixpanelEvents';
import { FIVE_SECONDS } from 'data/global';

const PopNotifications: React.FC = (props) => {
  const { children } = props;

  const [notifications, setNotifications] = useState<
    Array<Notification & { notId: string }>
  >([]);

  const notificationsRef = useRef(new Set<PopNotificationRef>());

  const getMixpanelProps = useGetMixpanelProps();

  const { t } = useTranslation();

  const onNotificationClose = useCallback((n: Notification) => {
    n.onClose?.();
    setNotifications((old) => old.filter((not) => not !== n));
  }, []);

  const popInfo = useCallback((conf: Notification) => {
    Array.from(notificationsRef.current)
      .find((n) => n.open)
      ?.setOpen(false);
    setNotifications((old) => [...old, { ...conf, notId: uniqid() }]);
  }, []);

  const popSuccess = useCallback(
    (content: string | ReactNode) => {
      popInfo({
        type: 'Success',
        content,
      });
    },
    [popInfo],
  );

  const popError = useCallback(
    (content?: string | ReactNode) => {
      popInfo({
        type: 'Error',
        content: content || <GenericErrorContent />,
      });
    },
    [popInfo],
  );

  const popServerError = useCallback(
    (error: AxiosError | unknown, overrideMessage = '') => {
      const axiosError = error as AxiosError;

      const id = getAxiosErrorId(axiosError, overrideMessage);

      mixpanelService.errorViewed(
        getMixpanelProps(
          getMixpanelErrorProps(error, overrideMessage),
        ) as ErrorViewedProps,
      );

      const errorMessageKey = ERRORS_USER_FRIENDLY[id as ErrorMessageValue];

      if (!overrideMessage && !errorMessageKey) {
        popError();

        return;
      }

      popError(overrideMessage || t(errorMessageKey));
    },
    [getMixpanelProps, popError, t],
  );

  return (
    <PopNotificationsContext.Provider
      value={{ popInfo, popSuccess, popError, popServerError }}
    >
      {notifications.map((n, ind) => (
        <PopNotification
          side="top"
          className="pop-notif-plat"
          dissappearAfter={FIVE_SECONDS}
          ref={(r) => r && notificationsRef.current.add(r)}
          key={n.notId}
          {...n}
          onClose={() => {
            notificationsRef.current.delete(
              Array.from(notificationsRef.current)[ind],
            );
            onNotificationClose?.(n);
          }}
        >
          {n.content}
        </PopNotification>
      ))}
      {children}
    </PopNotificationsContext.Provider>
  );
};

export default PopNotifications;
